import type { Child, Document, Manifest, Reference } from '../api/authoring'
import {
  arrangementsOf,
  linkIdsIn,
  linksIn,
  storedRegions,
  type Arrangement,
} from './arrangement'
import { resolve } from './schema'
import { childByLinkId, encodeLinkPath, isLinked, pinStateOf, type LinkPath, type PinState } from './link'
import { fieldsSchema, slotsToShow } from './manifest'

// What a document holds, arranged the way its types say — every node, not
// only the root's.
//
// **This replaced a Page-shaped reading.** `resolveSlots` joined
// `fields.slots` to `children`, which was correct for the one type that
// spells its regions that way and described nothing about a Container's
// `items` or a Grid's `cells`. So an inline Container inside a page was
// drawn, selectable, and impossible to put anything into — the tree went
// one level deep in controls and any number of levels deep in display.
//
// Now the join is made from the arrangement the *fields schema* declares,
// at every level, so a node's placements are readable wherever it sits
// and for a type this client has never seen.
//
// The recursion stops at a linked child, and that is the model's rule
// rather than a limit here: a linked child's contents live in its own
// stream and this document does not hold them, so drawing them would be
// claiming to show something that was never fetched.

/** One region of a node, with what is in it. */
export interface CompositionRegion {
  /** The region's name. Absent where the type keeps a single unnamed list. */
  slot?: string
  /**
   * Whether the type declares this region name. Always true for an
   * unnamed one — there is no vocabulary for it to be outside of.
   */
  declared: boolean
  /**
   * Whether the holder's *slot-carrying* intents serve this region. It is
   * what a control passes to `intentArranging`, so the question "which
   * intent places here" is answered from the same fact that produced the
   * region.
   */
  keyed: boolean
  placements: CompositionNode[]
  /** Link ids the arrangement names that the document has no child for. */
  dangling: string[]
  /**
   * The arrangement's whole stored order, dangling entries included.
   *
   * A reorder must send the complete order — one that omits an entry
   * *keeps* it, at the end — so a control that rebuilt the order from
   * `placements` alone would quietly move every dangling id to the
   * bottom on the next move. It is also the length a new placement
   * appends at.
   */
  linkIds: string[]
}

/** One node of a document: the root, or a placement inside it. */
export interface CompositionNode {
  /** The link path from the document root. Empty at the root. */
  path: LinkPath
  type: string
  fields: Record<string, unknown>
  /** References held by this node. */
  references: Reference[]
  /** The placement's own id. Absent at the root, which is not placed. */
  linkId?: string
  pin?: PinState
  /** A linked child's target id. Absent for an inline node. */
  targetId?: string
  /** True when this node's content lives in another stream. */
  linked: boolean
  depth: number
  /** Empty for a linked node, whose arrangement this document cannot see. */
  regions: CompositionRegion[]
  /**
   * Children no arrangement of this node names. They compose nowhere, the
   * model permits it, and an author has no other way to find one.
   */
  unarranged: CompositionNode[]
  /**
   * Arrangements this client has no control for — a website's routing
   * tree, which is recursive and carries a slug per node. Named rather
   * than hidden, so a component holding placements never looks empty.
   */
  undrawn: Arrangement[]
}

/**
 * One region of one node, which is what a drag's two ends actually are.
 *
 * Carried through the drag rather than looked up afterwards from a
 * string: a group id is built to be compared, and a control that parsed
 * one back into a node would be holding a second, worse copy of the tree
 * it is already rendering.
 */
export interface RegionRef {
  node: CompositionNode
  region: CompositionRegion
}

/**
 * The id a drag compares two regions by.
 *
 * The node's path *and* the region's name, because a page and a section
 * inside it may both have a region called `body`, and a drag that could
 * not tell them apart would offer to move a block into the list it is
 * already in.
 */
export function regionGroup(node: CompositionNode, region: CompositionRegion): string {
  return `${encodeLinkPath(node.path)}#${region.slot ?? ''}`
}

/**
 * Whether a type keeps placements at all.
 *
 * Asked before drawing a composition panel, so a Content or a Media —
 * which arrange nothing — get no heading over an empty list. It is the
 * type's schema that answers, not the document, because a Gallery with no
 * images still arranges images and should say so with an Add button.
 */
export function arranges(manifest: Manifest | undefined, typeName: string): boolean {
  const schema = fieldsSchema(manifest, typeName)
  return schema ? arrangementsOf(schema, schema).length > 0 : false
}

/** Read a whole document as a tree of nodes and their regions. */
export function composeDocument(manifest: Manifest | undefined, document: Document): CompositionNode {
  return compose(
    manifest,
    {
      type: document.type,
      fields: (document.fields ?? {}) as Record<string, unknown>,
      children: document.children,
      references: document.references,
    },
    [],
    0,
  )
}

function compose(
  manifest: Manifest | undefined,
  node: {
    type: string
    fields: Record<string, unknown>
    children: Child[] | undefined
    references: Reference[] | undefined
  },
  path: LinkPath,
  depth: number,
): CompositionNode {
  const schema = fieldsSchema(manifest, node.type)
  const arrangements = schema ? arrangementsOf(schema, schema) : []

  const regions: CompositionRegion[] = []
  const undrawn: Arrangement[] = []

  for (const arrangement of arrangements) {
    // `single` and `tree` are real arrangements this client offers no
    // control for. Both are a Website's, and both belong to the screen
    // that edits a site rather than to a list of placements.
    if (arrangement.kind === 'single' || arrangement.kind === 'tree') {
      undrawn.push(arrangement)
      continue
    }

    if (arrangement.kind === 'keyed') {
      for (const { slot, declared } of slotsToShow(
        manifest,
        node.type,
        storedRegions(arrangement, node.fields),
      )) {
        regions.push(
          region(manifest, node.children, linkIdsIn(arrangement, node.fields, slot), path, depth, {
            slot,
            declared,
            keyed: true,
          }),
        )
      }
      continue
    }

    regions.push(
      region(manifest, node.children, linkIdsIn(arrangement, node.fields), path, depth, {
        declared: true,
        keyed: false,
      }),
    )
  }

  // By difference, and against *every* arrangement rather than the drawn
  // ones. A website's routing tree holds the placement for every page it
  // routes; counting only what is drawn would report each of them as
  // composing nowhere, which is both false and the most alarming thing
  // this client could say to somebody looking at their own site.
  const properties = schema ? (resolve(schema, schema)?.properties ?? {}) : {}
  const arranged = new Set(
    arrangements.flatMap((arrangement) =>
      schema ? linksIn(properties[arrangement.property], node.fields[arrangement.property], schema) : [],
    ),
  )
  const unarranged = (node.children ?? [])
    .filter((child) => !arranged.has(child.linkId))
    .map((child) => placement(manifest, child, path, depth))

  return {
    path,
    type: node.type,
    fields: node.fields,
    references: node.references ?? [],
    linked: false,
    depth,
    regions,
    unarranged,
    undrawn,
  }
}

function region(
  manifest: Manifest | undefined,
  children: Child[] | undefined,
  linkIds: string[],
  path: LinkPath,
  depth: number,
  meta: { slot?: string; declared: boolean; keyed: boolean },
): CompositionRegion {
  const placements: CompositionNode[] = []
  const dangling: string[] = []

  for (const linkId of linkIds) {
    const child = childByLinkId(children, linkId)
    if (!child) {
      dangling.push(linkId)
      continue
    }
    placements.push(placement(manifest, child, path, depth))
  }

  return { ...meta, placements, dangling, linkIds }
}

function placement(
  manifest: Manifest | undefined,
  child: Child,
  parentPath: LinkPath,
  depth: number,
): CompositionNode {
  const path = [...parentPath, child.linkId]

  if (isLinked(child)) {
    return {
      path,
      type: child.type,
      fields: (child.fields ?? {}) as Record<string, unknown>,
      references: child.references ?? [],
      linkId: child.linkId,
      pin: pinStateOf(child),
      targetId: child.id,
      linked: true,
      depth: depth + 1,
      regions: [],
      unarranged: [],
      undrawn: [],
    }
  }

  const inline = compose(
    manifest,
    {
      type: child.type,
      fields: (child.fields ?? {}) as Record<string, unknown>,
      children: child.children,
      references: child.references,
    },
    path,
    depth + 1,
  )
  return { ...inline, linkId: child.linkId, pin: pinStateOf(child), depth: depth + 1 }
}
